import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Tag,
    Tooltip,
    Tabs,
    DatePicker,
    Select,
    Table,
    Badge,
    Space,
    Radio,
    Button,
    Modal,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { CloseCircleOutlined, FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs, { Dayjs } from "dayjs";

import Loader from "../../../../../../core/common/loader";
import CollapseHeader from "../../../../../../core/common/collapse-header/collapse-header";
import CustomPagination from "../../../../../../core/common/customPagination/CustomPagination";
import { all_routes } from "../../../../../router/all_routes";
import { notify } from "../../../../../../services/toast.config";
import api from "../../../../../../services/api";
import { getInitials } from "../../../../../../helper/avatarHelper";
import { humanReadableDate } from "../../../../../../helper/dateTimeHelper";


const { RangePicker } = DatePicker;

interface LeaveRecord {
    id: number;
    leaveTypeName: string;
    leaveCategory: string;
    leaveTypeId?: number;
    startDate: string;
    endDate: string;
    days: number;
    reason: string;
    status: string;
    isPlanned: boolean;
    createdAt: string;
    approverName?: string;
    approverImage?: string;
    documents: any[];
    rejectionReason?: string;
    startDayPart?: string;
    endDayPart?: string;
}

interface LeaveBalance {
    leaveTypeId: number;
    leaveTypeName: string;
    unlimited: boolean;
    isMonthlyAccrual: boolean;
    total: number;
    used: number;
    pending: number;
    remaining: number;
    leaveCategory?: string;
    carryForwardFromPrevMonth?: number;
}

interface EmployeeInfo {
    id: number;
    name: string;
    email: string;
    department: string;
    designation: string;
    profile_pic?: string;
    joinDate?: string;
    mobile_no?: string;
}

interface SummaryStats {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
    cancelled: number;
}

interface LeaveBalanceSummary {
    total: number;
    used: number;
    pending: number;
    remaining: number;
    carriedForward: number;
}

const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
        approved: "success",
        rejected: "error",
        pending: "warning",
        cancelled: "default",
    };
    return statusMap[status.toLowerCase()] || "default";
};

const LeaveSummaryPage: React.FC = () => {
    const { empId } = useParams<{ empId: string }>();

    const [isPageLoading, setIsPageLoading] = useState(false);
    const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
    const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);

    // ✅ Store ALL leave history (unfiltered from backend)
    const [allLeaveHistory, setAllLeaveHistory] = useState<LeaveRecord[]>([]);

    const [leaveBalanceSummary, setLeaveBalanceSummary] = useState<LeaveBalanceSummary>({
        total: 0,
        used: 0,
        pending: 0,
        remaining: 0,
        carriedForward: 0,
    });

    // ✅ Frontend filters only
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [leaveTypeFilter, setLeaveTypeFilter] = useState<number | null>(null);

    // ✅ Date range (triggers backend call)
    const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
    const [dateRangeType, setDateRangeType] = useState<"current" | "last" | "custom">("current");

    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 30,
        total: 0,
        showTotal: (total, range) => `Showing ${range[0]} - ${range[1]} of ${total} entries`,
    });

    const [reasonModalOpen, setReasonModalOpen] = useState(false);
    const [reasonModalText, setReasonModalText] = useState<string>("");
    const REASON_PREVIEW_LIMIT = 90;

    const openReasonModal = (text: string) => {
        setReasonModalText(text);
        setReasonModalOpen(true);
    };

    const getMonthDateRange = (
        type: "current" | "last"
    ): [Dayjs, Dayjs] => {
        const now = dayjs();
        if (type === "current") {
            const startOfMonth = now.startOf("month");
            const endOfMonth = now.endOf("month");
            return [startOfMonth, endOfMonth];
        } else {
            const lastMonth = now.subtract(1, "month");
            const startOfLastMonth = lastMonth.startOf("month");
            const endOfLastMonth = lastMonth.endOf("month");
            return [startOfLastMonth, endOfLastMonth];
        }
    };

    const handleDateRangeTypeChange = (e: any) => {
        const type = e.target.value;
        setDateRangeType(type);

        if (type === "current") {
            setDateRange([null, null]);
        } else if (type === "last") {
            const [start, end] = getMonthDateRange("last");
            setDateRange([start, end]);
        }
    };

    // ✅ Load employee summary - NO status/leaveType filters
    const loadEmployeeSummary = useCallback(async () => {
        if (!empId) {
            notify.error("Employee ID is required");
            return;
        }

        try {
            setIsPageLoading(true);
            const signature = localStorage.getItem("signature");

            let apiStartDate = null;
            let apiEndDate = null;

            if (dateRangeType === "custom" && dateRange[0] && dateRange[1]) {
                apiStartDate = dateRange[0].format("YYYY-MM-DD");
                apiEndDate = dateRange[1].format("YYYY-MM-DD");
            } else if (dateRangeType === "last") {
                const [start, end] = getMonthDateRange("last");
                apiStartDate = start.format("YYYY-MM-DD");
                apiEndDate = end.format("YYYY-MM-DD");
            }

            const response = await api.post("attendanceService/get-employee-leave-summary", {
                signature,
                employeeId: Number(empId),
                startDate: apiStartDate,
                endDate: apiEndDate,
                page: 1,
                limit: 100,
            });

            const data = response.data?.data?.[0] || response.data?.data || {};

            setEmployeeInfo(data.employeeInfo || null);
            setLeaveBalances(data.leaveBalances || []);
            setAllLeaveHistory(data.leaveHistory || []);

            setLeaveBalanceSummary(data.leaveBalanceSummary || {
                total: 0,
                used: 0,
                pending: 0,
                remaining: 0,
                carriedForward: 0,
            });
            setPagination(prev => ({
                ...prev,
                current: 1,
            }));
        } catch (error: any) {
            const errMsg = error?.response?.data?.message || error?.message || "Failed to load employee leave summary";
            notify.error(errMsg);
        } finally {
            setIsPageLoading(false);
        }
    }, [empId, dateRange]);

    useEffect(() => {
        loadEmployeeSummary();
    }, [loadEmployeeSummary]);

    useEffect(() => {
        const [start, end] = getMonthDateRange("current");
        setDateRange([start, end]);
    }, []);

    const { filteredLeaveHistory, summaryStats } = useMemo(() => {
        let filtered = [...allLeaveHistory];
        if (statusFilter !== "all") {
            filtered = filtered.filter(leave => leave.status === statusFilter);
        }

        if (leaveTypeFilter) {
            filtered = filtered.filter(leave => {
                if (leave.leaveTypeId) {
                    return leave.leaveTypeId === leaveTypeFilter;
                }
                const selectedLeaveType = leaveBalances.find(b => b.leaveTypeId === leaveTypeFilter);
                return leave.leaveTypeName === selectedLeaveType?.leaveTypeName;
            });
        }

        const stats = {
            total: allLeaveHistory.length,
            approved: allLeaveHistory.filter(l => l.status === "approved").length,
            pending: allLeaveHistory.filter(l => l.status === "pending").length,
            rejected: allLeaveHistory.filter(l => l.status === "rejected").length,
            cancelled: allLeaveHistory.filter(l => l.status === "cancelled").length,
        };

        return {
            filteredLeaveHistory: filtered,
            summaryStats: stats,
        };
    }, [allLeaveHistory, statusFilter, leaveTypeFilter, leaveBalances]);

    // ✅ Frontend pagination
    const paginatedLeaveHistory = useMemo(() => {
        const startIndex = (pagination.current! - 1) * pagination.pageSize!;
        const endIndex = startIndex + pagination.pageSize!;
        return filteredLeaveHistory.slice(startIndex, endIndex);
    }, [filteredLeaveHistory, pagination.current, pagination.pageSize]);

    // ✅ Update total when filtered data changes
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            total: filteredLeaveHistory.length,
            current: 1, // Reset to page 1 when filters change
        }));
    }, [filteredLeaveHistory.length]);

    const handleClearLeaveType = () => {
        setLeaveTypeFilter(null);
    };

    const handleClearDateRange = () => {
        setDateRangeType("current");
        const [start, end] = getMonthDateRange("current");
        setDateRange([start, end]);
    };

    const getActiveFiltersCount = () => {
        let count = 0;
        if (statusFilter !== "all") count++;
        if (leaveTypeFilter) count++;
        if (dateRangeType !== "current") count++;
        return count;
    };

    const handleResetFilters = () => {
        setLeaveTypeFilter(null);
        setStatusFilter("all");
        setDateRangeType("current");
        const [start, end] = getMonthDateRange("current");
        setDateRange([start, end]);
    };

    const calculateBreakdownTotals = () => {
        const nonUnlimitedLeaves = leaveBalances.filter((b) => !b.unlimited);
        const allLeaves = leaveBalances;
        return {
            total: nonUnlimitedLeaves.reduce((sum, b) => sum + (b.total || 0), 0),
            used: allLeaves.reduce((sum, b) => sum + (b.used || 0), 0),
            pending: allLeaves.reduce((sum, b) => sum + (b.pending || 0), 0),
            remaining: nonUnlimitedLeaves.reduce((sum, b) => sum + (b.remaining || 0), 0),
            carriedForward: nonUnlimitedLeaves.reduce((sum, b) => sum + (b.carryForwardFromPrevMonth || 0), 0),
        };
    };

    const calculateCategoryBreakdowns = () => {
        const nonUnlimitedLeaves = leaveBalances.filter((b) => !b.unlimited);
        const allLeaves = leaveBalances;

        const shortLeaves = allLeaves.filter(b =>
            b.leaveTypeName.toLowerCase().includes('short leave')
        );
        const unpaidLeaves = allLeaves.filter(b =>
            b.leaveCategory?.toLowerCase() === 'other'
        );
        const paidLeaves = allLeaves.filter(b =>
            !b.leaveTypeName.toLowerCase().includes('short leave') &&
            b.leaveCategory?.toLowerCase() !== 'other'
        );

        return {
            total: {
                short: nonUnlimitedLeaves
                    .filter(b => b.leaveTypeName.toLowerCase().includes('short leave'))
                    .reduce((sum, b) => sum + (b.total || 0), 0),
                paid: nonUnlimitedLeaves
                    .filter(b => !b.leaveTypeName.toLowerCase().includes('short leave') && b.leaveCategory?.toLowerCase() !== 'other')
                    .reduce((sum, b) => sum + (b.total || 0), 0),
            },
            used: {
                short: shortLeaves.reduce((sum, b) => sum + (b.used || 0), 0),
                paid: paidLeaves.reduce((sum, b) => sum + (b.used || 0), 0),
                unpaid: unpaidLeaves.reduce((sum, b) => sum + (b.used || 0), 0),
            },
            pending: {
                short: shortLeaves.reduce((sum, b) => sum + (b.pending || 0), 0),
                paid: paidLeaves.reduce((sum, b) => sum + (b.pending || 0), 0),
                unpaid: unpaidLeaves.reduce((sum, b) => sum + (b.pending || 0), 0),
            },
            remaining: {
                short: nonUnlimitedLeaves
                    .filter(b => b.leaveTypeName.toLowerCase().includes('short leave'))
                    .reduce((sum, b) => sum + (b.remaining || 0), 0),
                paid: nonUnlimitedLeaves
                    .filter(b => !b.leaveTypeName.toLowerCase().includes('short leave') && b.leaveCategory?.toLowerCase() !== 'other')
                    .reduce((sum, b) => sum + (b.remaining || 0), 0),
            },
        };
    };

    const breakdownTotals = calculateBreakdownTotals();
    const categoryBreakdowns = calculateCategoryBreakdowns();

    const getSelectedLeaveTypeName = () => {
        if (!leaveTypeFilter) return null;
        const selectedBalance = leaveBalances.find(b => b.leaveTypeId === leaveTypeFilter);
        return selectedBalance?.leaveTypeName || null;
    };

    const selectedLeaveTypeName = getSelectedLeaveTypeName();

    const formatDayPart = (part: string | any) => {
        switch (part) {
            case "first_half":
                return "First Half";
            case "second_half":
                return "Second Half";
            case "full":
                return "Full Day";
            default:
                return part || "--";
        }
    };

    const columns: ColumnsType<LeaveRecord> = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
            render: (text) => (
                <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>
                    #{text}
                </span>
            ),
        },
        {
            title: "Leave Type",
            dataIndex: "leaveTypeName",
            key: "leaveTypeName",
            width: 140,
            render: (text) => (
                <span style={{ fontSize: 13.5, fontWeight: 500, color: "#0f172a" }}>
                    {text}
                </span>
            ),
        },
        {
            title: "From",
            dataIndex: "startDate",
            key: "startDate",
            width: 140,
            sorter: (a: any, b: any) => moment(a.startDate).diff(moment(b.startDate)),
            render: (text: any, record: any) => (
                <div className="d-flex flex-column">
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
                        {humanReadableDate(text)}
                    </span>
                    <span
                        className="bg-transparent-purple text-purple px-1 mt-1"
                        style={{ fontSize: 11, width: "fit-content", borderRadius: 4 }}
                    >
                        {formatDayPart(record?.startDayPart)}
                    </span>
                </div>
            ),
        },
        {
            title: "To",
            dataIndex: "endDate",
            key: "endDate",
            width: 140,
            sorter: (a: any, b: any) => moment(a.endDate).diff(moment(b.endDate)),
            render: (text: any, record: any) => (
                <div className="d-flex flex-column">
                    <span style={{ fontSize: 13, fontWeight: 500, color: "#0f172a" }}>
                        {humanReadableDate(text)}
                    </span>
                    <span
                        className="bg-transparent-purple text-purple px-1 mt-1"
                        style={{ fontSize: 11, width: "fit-content", borderRadius: 4 }}
                    >
                        {formatDayPart(record?.endDayPart)}
                    </span>
                </div>
            ),
        },
        {
            title: "Days",
            dataIndex: "days",
            key: "days",
            width: 80,
            align: "center",
            render: (days: number) => (
                <span
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 28,
                        height: 24,
                        padding: "0 8px",
                        background: "#1d71e3",
                        color: "#ffffff",
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: 4,
                    }}
                >
                    {days}
                </span>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 110,
            align: "center",
            render: (status: string) => (
                <Tag
                    color={getStatusColor(status)}
                    style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.5, margin: 0 }}
                >
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: "Approved By",
            dataIndex: "approverName",
            key: "approverName",
            width: 140,
            render: (name) => (
                <span style={{ fontSize: 13, color: name ? "#0f172a" : "#94a3b8" }}>
                    {name || "—"}
                </span>
            ),
        },
        {
            title: "Applied On",
            dataIndex: "createdAt",
            key: "createdAt",
            width: 220,
            render: (date) => (
                <Tooltip title={humanReadableDate(date, true)}>
                    <span
                        className="d-inline-flex align-items-center"
                        style={{
                            fontSize: 11.5,
                            fontWeight: 500,
                            color: "#b45309",
                            background: "#fff7ed",
                            border: "1px solid #fed7aa",
                            padding: "4px 8px",
                            borderRadius: 6,
                        }}
                    >
                        {humanReadableDate(date, true)}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            width: 280,
            render: (reason: string) => {
                if (!reason) {
                    return <span style={{ color: "#94a3b8" }}>—</span>;
                }
                const isLong = reason.length > REASON_PREVIEW_LIMIT;
                const preview = isLong
                    ? `${reason.slice(0, REASON_PREVIEW_LIMIT).trim()}…`
                    : reason;
                return (
                    <div style={{ maxWidth: 280 }}>
                        <span
                            style={{
                                fontSize: 12.5,
                                color: "#475569",
                                lineHeight: 1.5,
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                            }}
                        >
                            {preview}
                        </span>
                        {isLong && (
                            <button
                                type="button"
                                className="btn btn-link p-0 ms-1"
                                style={{
                                    fontSize: 12,
                                    fontWeight: 500,
                                    verticalAlign: "baseline",
                                    textDecoration: "none",
                                }}
                                onClick={() => openReasonModal(reason)}
                            >
                                Read more
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    const statusTabs = [
        {
            key: "all",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-list-check me-2" />
                    <span className="fw-medium">All</span>
                    <Badge
                        count={summaryStats.total}
                        showZero
                        className="ms-2"
                        style={{ fontSize: 12, backgroundColor: "#1890ff" }}
                    />
                </span>
            ),
        },
        {
            key: "approved",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-checks me-2 text-success" />
                    <span className="fw-medium">Approved</span>
                    <Badge
                        count={summaryStats.approved}
                        showZero
                        className="ms-2"
                        style={{ fontSize: 12, backgroundColor: "#52c41a" }}
                    />
                </span>
            ),
        },
        {
            key: "pending",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-clock me-2 text-warning" />
                    <span className="fw-medium">Pending</span>
                    <Badge
                        count={summaryStats.pending}
                        showZero
                        className="ms-2"
                        style={{ fontSize: 12, backgroundColor: "#faad14" }}
                    />
                </span>
            ),
        },
        {
            key: "rejected",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-x me-2 text-danger" />
                    <span className="fw-medium">Rejected</span>
                    <Badge
                        count={summaryStats.rejected}
                        showZero
                        className="ms-2"
                        style={{ fontSize: 12, backgroundColor: "#ff4d4f" }}
                    />
                </span>
            ),
        },
        {
            key: "cancelled",
            label: (
                <span className="d-flex align-items-center">
                    <i className="ti ti-ban me-2 text-secondary" />
                    <span className="fw-medium">Cancelled</span>
                    <Badge
                        count={summaryStats.cancelled}
                        showZero
                        className="ms-2"
                        style={{ fontSize: 12, backgroundColor: "#d9d9d9" }}
                    />
                </span>
            ),
        },
    ];

    return (
        <div className="page-wrapper">

            <div className="content">
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">Employee Leave Summary</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">Employee</li>
                                <li className="breadcrumb-item">Leaves</li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    Summary
                                </li>
                            </ol>
                        </nav>
                    </div>

                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                        <div className="me-2 mb-2">
                            <Link
                                to={all_routes?.leaveadmin}
                                className="btn btn-info d-inline-flex align-items-center"
                            >
                                <i className="ti ti-arrow-back me-2" />
                                Go Back
                            </Link>
                        </div>
                        <div className="ms-2 head-icons">
                            <CollapseHeader />
                        </div>
                    </div>
                </div>

                {isPageLoading ? <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 600 }}>
                    <div className="text-center">
                        <div
                            className="spinner-border text-primary"
                            role="status"
                            style={{ width: "3rem", height: "3rem" }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading summary...</p>
                    </div>
                </div> : <>
                    {employeeInfo && (
                        <div className="card mb-3">
                            <div className="card-body bg-white rounded-2">
                                <div className="d-flex align-items-center">
                                    <div className="avatar avatar-xxxl me-3">
                                        {employeeInfo.profile_pic ? (
                                            <img
                                                src={employeeInfo.profile_pic}
                                                alt={employeeInfo.name}
                                                className="rounded-circle"
                                            />
                                        ) : (
                                            <div style={{
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: "50%",
                                                backgroundColor: "#007bff",
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontWeight: "bold",
                                            }}>
                                                {getInitials(employeeInfo?.name)}
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex-grow-1">
                                        <h4 className="mb-1">{employeeInfo.name}</h4>
                                        <p className="text-muted mb-1">
                                            {employeeInfo.designation} • <span className="badge badge-sm badge-soft-primary">{employeeInfo.department}</span>
                                        </p>
                                        <p className="text-muted small mb-0">
                                            <i className="ti ti-mail me-1" />
                                            {employeeInfo.email}
                                            {employeeInfo.mobile_no && (
                                                <span className="ms-3">
                                                    <i className="ti ti-phone me-1" />
                                                    {employeeInfo.mobile_no}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row mb-3">
                        <div className="col-xl-3 col-sm-6">
                            <div className="card">
                                <div className="card-body bg-white rounded-3 p-3">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div>
                                            <span className="d-block mb-2 text-muted fw-medium" style={{ fontSize: '13px' }}>Total</span>
                                            <h3 className="mb-0 text-primary fw-bold">{leaveBalanceSummary.total.toFixed(1)}</h3>
                                        </div>
                                        <div className="avatar avatar-lg bg-light-primary rounded">
                                            <i className="ti ti-calendar-stats text-primary fs-24" />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                        <div className="text-center flex-fill">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Short</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.total.short.toFixed(1)}</div>
                                        </div>
                                        <div className="text-center flex-fill border-start">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Paid</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.total.paid.toFixed(1)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3 col-sm-6">
                            <div className="card">
                                <div className="card-body bg-white rounded-3 p-3">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div>
                                            <span className="d-block mb-2 text-muted fw-medium" style={{ fontSize: '13px' }}>Used</span>
                                            <h3 className="mb-0 text-danger fw-bold">{leaveBalanceSummary.used.toFixed(1)}</h3>
                                        </div>
                                        <div className="avatar avatar-lg bg-light-danger rounded">
                                            <i className="ti ti-calendar-minus text-danger fs-24" />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                        <div className="text-center flex-fill">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Short</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.used.short.toFixed(1)}</div>
                                        </div>
                                        <div className="text-center flex-fill border-start">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Paid</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.used.paid.toFixed(1)}</div>
                                        </div>
                                        <div className="text-center flex-fill border-start">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Unpaid</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.used.unpaid.toFixed(1)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3 col-sm-6">
                            <div className="card">
                                <div className="card-body bg-white rounded-3 p-3">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div>
                                            <span className="d-block mb-2 text-muted fw-medium" style={{ fontSize: '13px' }}>Pending</span>
                                            <h3 className="mb-0 text-warning fw-bold">{leaveBalanceSummary.pending.toFixed(1)}</h3>
                                        </div>
                                        <div className="avatar avatar-lg bg-light-warning rounded">
                                            <i className="ti ti-clock text-warning fs-24" />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                        <div className="text-center flex-fill">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Short</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.pending.short.toFixed(1)}</div>
                                        </div>
                                        <div className="text-center flex-fill border-start">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Paid</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.pending.paid.toFixed(1)}</div>
                                        </div>
                                        <div className="text-center flex-fill border-start">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Unpaid</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.pending.unpaid.toFixed(1)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-xl-3 col-sm-6">
                            <div className="card">
                                <div className="card-body bg-white rounded-3 p-3">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div>
                                            <span className="d-block mb-2 text-muted fw-medium" style={{ fontSize: '13px' }}>Remaining</span>
                                            <h3 className="mb-0 text-success fw-bold">{leaveBalanceSummary.remaining.toFixed(1)}</h3>
                                        </div>
                                        <div className="avatar avatar-lg bg-light-success rounded">
                                            <i className="ti ti-calendar-check text-success fs-24" />
                                        </div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                        <div className="text-center flex-fill">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Short</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.remaining.short.toFixed(1)}</div>
                                        </div>
                                        <div className="text-center flex-fill border-start">
                                            <div className="text-muted mb-1" style={{ fontSize: '11px', fontWeight: 500 }}>Paid</div>
                                            <div className="fw-bold text-dark" style={{ fontSize: '15px' }}>{categoryBreakdowns.remaining.paid.toFixed(1)}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mb-3">
                        <div className="card-header">
                            <h5 className="mb-0">Leave Balance Breakdown</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Leave Type</th>
                                            <th>Total</th>
                                            <th>Carried Fwd</th>
                                            <th>Used</th>
                                            <th>Pending</th>
                                            <th>Remaining</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leaveBalances.map((balance) => (
                                            <tr key={balance.leaveTypeId}>
                                                <td>
                                                    <span className="fw-semibold">{balance.leaveTypeName}</span>
                                                    {balance.unlimited && <Tag color="purple" className="ms-2">Unlimited</Tag>}
                                                    {balance.isMonthlyAccrual && <Tag color="blue" className="ms-2">Monthly</Tag>}
                                                </td>
                                                <td className="fw-bold">{balance.unlimited ? "∞" : balance.total.toFixed(1)}</td>
                                                <td>
                                                    <span className="text-muted fw-bold">{balance.unlimited ? "—" : (balance.carryForwardFromPrevMonth || 0).toFixed(1)}</span>
                                                </td>
                                                <td>
                                                    <span className="text-danger fw-bold">{balance.used.toFixed(1)}</span>
                                                </td>
                                                <td>
                                                    <span className="text-info fw-bold">{balance.pending.toFixed(1)}</span>
                                                </td>
                                                <td>
                                                    <span className="text-success fw-bold">
                                                        {balance.unlimited ? "∞" : balance.remaining.toFixed(1)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="table-active">
                                            <td>
                                                <span className="fw-bold text-dark">Total</span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {breakdownTotals.total.toFixed(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold text-muted">
                                                    {breakdownTotals.carriedForward.toFixed(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold text-danger">
                                                    {breakdownTotals.used.toFixed(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold text-info">
                                                    {breakdownTotals.pending.toFixed(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold text-success">
                                                    {breakdownTotals.remaining.toFixed(1)}
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header border-0 pb-0">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                    <h4 className="mb-1 d-flex align-items-center">
                                        <i className="ti ti-history text-primary fs-24 me-2" />
                                        Leave History
                                    </h4>
                                    <p className="text-muted mb-0 small">Complete leave application history</p>
                                </div>
                            </div>

                            {/* ✅ Filters card */}
                            <div className="card border-0 mb-3">
                                <div className="card-body p-3 bg-transparent-info">
                                    <div className="row g-3 mb-3">
                                        {/* Leave Type Filter - Frontend only */}
                                        <div className="col-md-3">
                                            <label className="form-label small fw-semibold mb-2">
                                                <i className="ti ti-filter me-1" />
                                                Leave Type
                                            </label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Select
                                                    style={{ flex: 1 }}
                                                    placeholder="Select Leave Type"
                                                    value={leaveTypeFilter}
                                                    onChange={(value) => setLeaveTypeFilter(value)}
                                                    allowClear={false}
                                                    size="middle"
                                                >
                                                    <Select.Option value={null}>
                                                        <Space>
                                                            <i className="ti ti-list" />
                                                            <span>All Leave Types</span>
                                                        </Space>
                                                    </Select.Option>
                                                    {leaveBalances.map((balance) => (
                                                        <Select.Option key={balance.leaveTypeId} value={balance.leaveTypeId}>
                                                            <Space>
                                                                <i className="ti ti-calendar" />
                                                                <span>{balance.leaveTypeName}</span>
                                                            </Space>
                                                        </Select.Option>
                                                    ))}
                                                </Select>

                                                {leaveTypeFilter && (
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        danger
                                                        icon={<CloseCircleOutlined />}
                                                        onClick={handleClearLeaveType}
                                                        title="Clear Leave Type"
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Date Period Filter - Triggers API call */}
                                        <div className="col-md-9">
                                            <label className="form-label small fw-semibold mb-2">
                                                <i className="ti ti-calendar me-1" />
                                                Date Period
                                            </label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Radio.Group
                                                    value={dateRangeType}
                                                    onChange={handleDateRangeTypeChange}
                                                    className="d-flex gap-3"
                                                >
                                                    <Radio value="current">
                                                        <Space size={4}>
                                                            <span>Current Month</span>
                                                            <span className="text-muted small">({dayjs().format("MMM YYYY")})</span>
                                                        </Space>
                                                    </Radio>
                                                    <Radio value="last">
                                                        <Space size={4}>
                                                            <span>Last Month</span>
                                                            <span className="text-muted small">({dayjs().subtract(1, "month").format("MMM YYYY")})</span>
                                                        </Space>
                                                    </Radio>
                                                    <Radio value="custom">
                                                        Custom Range
                                                    </Radio>
                                                </Radio.Group>

                                                {dateRangeType === "custom" && (
                                                    <>
                                                        <RangePicker
                                                            style={{ width: "200px" }}
                                                            format="DD-MM-YYYY"
                                                            value={dateRange}
                                                            onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
                                                            placeholder={["Start Date", "End Date"]}
                                                            size="middle"
                                                        />
                                                    </>
                                                )}

                                                {dateRangeType !== "current" && (
                                                    <button
                                                        style={{ width: "100px" }}
                                                        className="btn btn-sm btn-info p-2 mb-2"
                                                        onClick={handleClearDateRange}
                                                        title="Reset to current month"
                                                    >
                                                        <i className="ti ti-x me-1" />
                                                        Clear Date
                                                    </button>

                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ✅ Status tabs - Frontend filtering, instant response */}
                            <Tabs
                                activeKey={statusFilter}
                                onChange={(key) => setStatusFilter(key)}
                                type="line"
                                size="small"
                                className="modern-tabs"
                                items={statusTabs}
                            />
                        </div>

                        <div className="card-body p-0 border-0 rounded-3">
                            {paginatedLeaveHistory.length > 0 ? (
                                <div
                                    className="bg-white rounded-3"
                                    style={{
                                        overflow: "hidden",
                                        border: "1px solid #e2e8f0",
                                    }}
                                >
                                    <Table
                                        className="custom-data-table"
                                        columns={columns}
                                        dataSource={paginatedLeaveHistory}
                                        rowKey="id"
                                        size="middle"
                                        pagination={false}
                                        scroll={{ x: 1200 }}
                                        rowClassName={(_, index) =>
                                            index % 2 === 0
                                                ? "data-table-row-light"
                                                : "data-table-row-dark"
                                        }
                                    />
                                    <CustomPagination
                                        currentPage={pagination.current || 1}
                                        totalPages={Math.max(
                                            1,
                                            Math.ceil(
                                                (pagination.total || 0) /
                                                (pagination.pageSize || 30),
                                            ),
                                        )}
                                        total={pagination.total || 0}
                                        limit={pagination.pageSize || 30}
                                        hasNextPage={
                                            (pagination.current || 1) *
                                            (pagination.pageSize || 30) <
                                            (pagination.total || 0)
                                        }
                                        hasPrevPage={(pagination.current || 1) > 1}
                                        onPageChange={(page) =>
                                            setPagination((prev) => ({
                                                ...prev,
                                                current: page,
                                            }))
                                        }
                                        onLimitChange={(limit) =>
                                            setPagination((prev) => ({
                                                ...prev,
                                                pageSize: limit,
                                                current: 1,
                                            }))
                                        }
                                    />
                                </div>
                            ) : (
                                <div
                                    className="d-flex justify-content-center align-items-center bg-white"
                                    style={{
                                        minHeight: 320,
                                        borderRadius: 12,
                                        border: "1px solid #e2e8f0",
                                    }}
                                >
                                    <div className="text-center">
                                        <div className="mb-3">
                                            <i className="ti ti-calendar-off text-muted" style={{ fontSize: "4rem" }} />
                                        </div>
                                        <h5 className="mt-3 mb-2 fw-semibold">No Leave Records Found</h5>
                                        <p className="text-muted mb-0">No leave applications match the selected filters.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>}

            </div>

            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                <p className="mb-0">2025 © CMS.</p>
                <p className="mb-0">
                    Designed & Developed By{" "}
                    <Link to="#" className="text-primary">
                        LGS
                    </Link>
                </p>
            </div>

            <Modal
                open={reasonModalOpen}
                onCancel={() => setReasonModalOpen(false)}
                title={
                    <span className="d-flex align-items-center">
                        <i className="ti ti-message-circle me-2 text-info" />
                        Leave Reason
                    </span>
                }
                footer={
                    <Button onClick={() => setReasonModalOpen(false)}>Close</Button>
                }
                destroyOnClose
                width={520}
                zIndex={1001}
            >
                <p
                    style={{
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: "#334155",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        marginBottom: 0,
                    }}
                >
                    {reasonModalText}
                </p>
            </Modal>

        </div>
    );
};

export default LeaveSummaryPage;
